import { useQuery } from '@tanstack/react-query';
import { apiClient } from 'api';
import {
  CREDIT_PRICE_FALLBACK,
  MAX_CREDIT_PURCHASE,
  MIN_CREDIT_PURCHASE,
  PURCHASED_CREDIT_VALIDITY_DAYS,
  WEEKLY_CREDIT_ALLOWANCE,
  type CreditCurrency,
} from 'utils/pricing';

export const CREDIT_PRICING_QUERY_KEY = ['creditPricing'];

export interface CreditPricing {
  /** Free credits granted every week. Do not roll over. */
  weeklyCredits: number;
  /** Smallest purchase the checkout accepts. */
  minPurchase: number;
  /** Largest purchase quotable at the flat rate. */
  maxPurchase: number;
  /** Volume at or above which pricing becomes a sales conversation. */
  volumeDiscountThreshold: number | null;
  /** Per-credit rate in each supported currency, flat at every volume. */
  pricePerCredit: Record<CreditCurrency, number>;
  /** Naira per credit — the currency top-ups are actually charged in. */
  pricePerCreditNgn: number;
  /** How long purchased credits remain spendable. */
  validityDays: number;
}

/** Used until the first response lands, and if the request fails. */
const FALLBACK: CreditPricing = {
  weeklyCredits: WEEKLY_CREDIT_ALLOWANCE,
  minPurchase: MIN_CREDIT_PURCHASE,
  maxPurchase: MAX_CREDIT_PURCHASE,
  volumeDiscountThreshold: null,
  pricePerCredit: CREDIT_PRICE_FALLBACK,
  pricePerCreditNgn: CREDIT_PRICE_FALLBACK.NGN,
  validityDays: PURCHASED_CREDIT_VALIDITY_DAYS,
};

const toPositive = (value: unknown, fallback: number): number => {
  const parsed = typeof value === 'string' ? Number(value) : value;
  return typeof parsed === 'number' && Number.isFinite(parsed) && parsed > 0 ? parsed : fallback;
};

/**
 * Credit pricing, straight from the server.
 *
 * `/billing/plans/` is the authority on what a credit costs and what the weekly
 * grant is; the constants in `utils/pricing` are only a first-paint fallback so
 * the public calculator renders instantly and survives an outage rather than
 * showing a blank or a zero price.
 *
 * The endpoint is unauthenticated, so the marketing site and the in-app top-up
 * modal quote from exactly the same source.
 */
export const useCreditPricing = () => {
  const query = useQuery({
    queryKey: CREDIT_PRICING_QUERY_KEY,
    queryFn: async (): Promise<CreditPricing> => {
      const response = await apiClient.get<any>('/billing/plans/');
      const data = response.data?.data ?? {};
      const ngn = toPositive(data.price_per_credit_ngn, FALLBACK.pricePerCredit.NGN);
      const usd = toPositive(data.price_per_credit_usd, FALLBACK.pricePerCredit.USD);
      return {
        weeklyCredits: toPositive(data.weekly_credits, FALLBACK.weeklyCredits),
        minPurchase: toPositive(data.min_credit_purchase, FALLBACK.minPurchase),
        maxPurchase: toPositive(data.max_credit_purchase, FALLBACK.maxPurchase),
        volumeDiscountThreshold:
          toPositive(data.volume_discount_threshold, 0) || null,
        pricePerCredit: { NGN: ngn, USD: usd },
        pricePerCreditNgn: ngn,
        validityDays: toPositive(data.purchased_credit_validity_days, FALLBACK.validityDays),
      };
    },
    staleTime: 30 * 60 * 1000, // pricing changes rarely
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const pricing = query.data ?? FALLBACK;

  return {
    pricing,
    isLoading: query.isLoading,
    /** True once the server's figures are in use rather than the fallback. */
    isServerBacked: query.isSuccess,
    /** Total price of an amount, in the given currency (naira by default). */
    priceFor: (credits: number, currency: CreditCurrency = 'NGN') =>
      credits * (pricing.pricePerCredit[currency] ?? pricing.pricePerCreditNgn),
  };
};
